import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../../api';
import Select from '../../components/Select';
import Field from '../../components/ui/Field';
import Estado from '../../components/ui/Estado';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/ui/PageHeader';
import BarChart from '../../components/ui/BarChart';
import DonutChart from '../../components/ui/DonutChart';
import DataTable from '../../components/ui/DataTable';
import Metrica, { MetricaGrid } from '../../components/ui/Metrica';
import { fmt, fmtBRL, fmtInt, fmtData } from '../../utils/format';
import { labelCategoria, labelTipoAplicacao } from '../../utils/categorias';
import { descreverArea } from '../../utils/unidades';
import { CORES_GRAFICO } from '../../utils/graficos';

const LS_SAFRA = 'relCustosLavoura.safraId';
const LS_TALHAO = 'relCustosLavoura.talhaoId';

export default function CustosPorLavoura() {
  const [safras, setSafras] = useState([]);
  const [safraId, setSafraId] = useState(localStorage.getItem(LS_SAFRA) || '');
  const [talhoes, setTalhoes] = useState([]);
  const [talhaoId, setTalhaoId] = useState(localStorage.getItem(LS_TALHAO) || '');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');

  const [relatorio, setRelatorio] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [erroSafras, setErroSafras] = useState('');

  // Safras — todas, inclusive fechadas: um relatório olha para trás, e o custo
  // de uma safra encerrada é justamente o que o produtor quer analisar.
  useEffect(() => {
    api.getSafras()
      .then((lista) => {
        const ord = [...(lista || [])].reverse();
        setSafras(ord);
        setSafraId((atual) => {
          if (ord.some((s) => String(s.id) === String(atual))) return atual;
          return ord.length ? String(ord[0].id) : '';
        });
      })
      .catch((e) => setErroSafras(e.message || 'Erro ao carregar safras.'));
  }, []);

  // Áreas plantadas da safra escolhida.
  useEffect(() => {
    if (!safraId) { setTalhoes([]); return; }
    api.getLavourasAtivasDaSafra(safraId)
      .then((list) => {
        const lista = list || [];
        setTalhoes(lista);
        setTalhaoId((atual) => {
          if (lista.some((t) => String(t.id) === String(atual))) return atual;
          return lista.length ? String(lista[0].id) : '';
        });
      })
      .catch(() => setTalhoes([]));
  }, [safraId]);

  useEffect(() => { if (safraId) localStorage.setItem(LS_SAFRA, safraId); }, [safraId]);
  useEffect(() => { if (talhaoId) localStorage.setItem(LS_TALHAO, talhaoId); }, [talhaoId]);

  const carregar = useCallback(async () => {
    if (!talhaoId) { setRelatorio(null); return; }
    setCarregando(true);
    setErro('');
    try {
      const r = await api.getRelatorioCustosLavoura(talhaoId, inicio || undefined, fim || undefined);
      setRelatorio(r);
    } catch (e) {
      setErro(e.message || 'Não foi possível carregar o relatório.');
      setRelatorio(null);
    } finally {
      setCarregando(false);
    }
  }, [talhaoId, inicio, fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const estufa = relatorio?.tipoArea === 'ESTUFA';
  const temRecorte = Boolean(inicio || fim);

  // ── Dados dos gráficos ──────────────────────────────────────────────────
  const dadosMes = useMemo(() => {
    if (!relatorio?.custosPorMes) return [];
    return relatorio.custosPorMes.map((m, i) => ({
      label: m.rotulo,
      value: m.custoTotalBRL || 0,
      color: CORES_GRAFICO[0],
    }));
  }, [relatorio]);

  const dadosCategoria = useMemo(() => {
    if (!relatorio?.custosPorCategoria) return [];
    return relatorio.custosPorCategoria.map((c, i) => ({
      label: labelCategoria(c.categoria),
      value: c.custoTotalBRL || 0,
      color: CORES_GRAFICO[i % CORES_GRAFICO.length],
    }));
  }, [relatorio]);

  const dadosTipo = useMemo(() => {
    if (!relatorio?.custosPorTipo) return [];
    return relatorio.custosPorTipo.map((t, i) => ({
      label: labelTipoAplicacao(t.tipoAplicacao),
      value: t.custoTotalBRL || 0,
      color: CORES_GRAFICO[i % CORES_GRAFICO.length],
    }));
  }, [relatorio]);

  // Aplicações agrupadas por dia. O backend já devolve ordenado por data desc,
  // então o grupo mais recente aparece primeiro; cada grupo soma o gasto do dia.
  const aplicacoesPorDia = useMemo(() => {
    const lista = relatorio?.aplicacoes || [];
    const grupos = new Map();
    for (const a of lista) {
      if (!grupos.has(a.data)) grupos.set(a.data, { data: a.data, total: 0, itens: [] });
      const g = grupos.get(a.data);
      g.itens.push(a);
      g.total += a.custoTotalBRL || 0;
    }
    return [...grupos.values()];
  }, [relatorio]);

  const semDados = relatorio && (relatorio.totalAplicacoes || 0) === 0;

  const msgVazio = (() => {
    if (carregando || erro) return null;
    if (safras.length === 0) {
      return {
        titulo: 'Cadastre uma safra',
        descricao: 'Crie uma safra e registre manejos para analisar os custos por lavoura.',
        acao: { to: '/safras', label: 'Ir para Safras' },
      };
    }
    if (talhoes.length === 0 && safraId) {
      return {
        titulo: 'Nenhuma área plantada nesta safra',
        descricao: 'Vincule uma área de cultivo à safra para começar a registrar manejos.',
        acao: { to: '/safras', label: 'Ir para Safras' },
      };
    }
    if (semDados) {
      return {
        titulo: 'Sem manejos no período',
        descricao: temRecorte
          ? 'Nenhuma aplicação encontrada nas datas escolhidas. Amplie o período ou limpe o filtro.'
          : 'Registre aplicações e custos nesta área para ver os gráficos.',
        acao: { to: `/custos?safra=${safraId}&talhao=${talhaoId}`, label: 'Lançar manejo' },
      };
    }
    return null;
  })();

  const limparPeriodo = () => { setInicio(''); setFim(''); };

  return (
    <div>
      <PageHeader
        titulo="Custos e aplicações por lavoura"
        icone="manejo"
        descricao="Quanto cada área custou, no que o dinheiro foi aplicado e quando — com gráficos por período, categoria e tipo de manejo."
        voltar={{ to: '/relatorios', label: 'Relatórios' }}
      >
        <div className="filtros">
          <Field label="Safra / Ciclo" obrigatorio>
            <Select
              value={safraId}
              onChange={setSafraId}
              options={safras.map((s) => ({ value: s.id, label: `${s.cultura || 'Safra'} — ${s.descricao}` }))}
              placeholder={erroSafras || (safras.length ? 'Selecione' : 'Nenhuma safra')}
              disabled={!safras.length}
            />
          </Field>
          <Field label="Área de cultivo" obrigatorio>
            <Select
              value={talhaoId}
              onChange={setTalhaoId}
              options={talhoes.map((t) => ({ value: t.id, label: descreverArea(t.lavoura, t.areaHectares) }))}
              placeholder={talhoes.length === 0 ? 'Nenhuma área plantada' : 'Selecione a área'}
              disabled={talhoes.length === 0}
            />
          </Field>
          <Field label="De">
            <input type="date" value={inicio} max={fim || undefined}
              onChange={(e) => setInicio(e.target.value)} />
          </Field>
          <Field label="Até" erro={inicio && fim && inicio > fim ? 'O fim deve ser depois do início.' : undefined}>
            <input type="date" value={fim} min={inicio || undefined}
              onChange={(e) => setFim(e.target.value)} />
          </Field>
          {temRecorte && (
            <Field label="Período">
              <button type="button" className="btn-ghost btn-sm" onClick={limparPeriodo}>
                Limpar
              </button>
            </Field>
          )}
        </div>
      </PageHeader>

      <Estado carregando={carregando} erro={erro} itens={4}>
        {msgVazio ? (
          <EmptyState
            compacto
            icone="manejo"
            titulo={msgVazio.titulo}
            descricao={msgVazio.descricao}
            acao={msgVazio.acao}
          />
        ) : relatorio ? (
          <>
            <MetricaGrid min={150}>
              <Metrica
                label="Custo total"
                value={fmtBRL(relatorio.custoTotalBRL)}
                tom="custo"
                destaque
                dica={temRecorte ? 'Soma dos manejos no período filtrado.' : 'Soma de todos os manejos desta área na safra.'}
              />
              <Metrica
                label={estufa ? 'Custo da estufa' : 'Custo por hectare'}
                value={relatorio.custoPorHectareBRL != null ? fmtBRL(relatorio.custoPorHectareBRL) : '—'}
                dica={estufa
                  ? 'Em estufa a metragem é em m², então este é o custo da estufa inteira.'
                  : 'Custo total dividido pela área plantada nesta safra.'}
              />
              {!temRecorte && relatorio.custoPorUnidadeBRL != null && (
                <Metrica
                  label={`Custo por ${(relatorio.unidadeProducao || 'unidade').toLowerCase()}`}
                  value={fmtBRL(relatorio.custoPorUnidadeBRL)}
                  dica="Quanto custou produzir cada unidade. Compare com o preço de venda para saber a margem."
                />
              )}
              <Metrica
                label="Aplicações"
                value={fmtInt(relatorio.totalAplicacoes)}
                detalhe={relatorio.primeiraAplicacao
                  ? `${fmtData(relatorio.primeiraAplicacao)} a ${fmtData(relatorio.ultimaAplicacao)}`
                  : undefined}
                dica="Número de lançamentos de manejo no período."
              />
            </MetricaGrid>

            <div className="grade-graficos">
              {dadosMes.length > 0 && (
                <div className="card">
                  <h2>Custo por período</h2>
                  <BarChart data={dadosMes} formatValue={fmtBRL} labelWidth={70} />
                  <p className="texto-ajuda">Gasto somado por mês de aplicação.</p>
                </div>
              )}

              {dadosCategoria.length > 0 && (
                <div className="card">
                  <h2>Gasto por categoria</h2>
                  <div className="grafico-centro">
                    <DonutChart
                      data={dadosCategoria}
                      centro={fmtBRL(relatorio.custoTotalBRL)}
                      centroSub="total"
                    />
                  </div>
                </div>
              )}

              {dadosTipo.length > 0 && (
                <div className="card">
                  <h2>Gasto por tipo de manejo</h2>
                  <BarChart data={dadosTipo} formatValue={fmtBRL} labelWidth={110} />
                </div>
              )}
            </div>

            {relatorio.custosPorCategoria?.length > 0 && (
              <div className="card">
                <h2>Resumo por categoria</h2>
                <DataTable
                  densa
                  linhas={relatorio.custosPorCategoria}
                  chaveDe={(c) => c.categoria}
                  colunas={[
                    { chave: 'categoria', titulo: 'Categoria', papel: 'titulo',
                      render: (c) => labelCategoria(c.categoria) },
                    { chave: 'custo', titulo: 'Custo', alinhar: 'direita', papel: 'destaque',
                      render: (c) => fmtBRL(c.custoTotalBRL) },
                    { chave: 'pct', titulo: '% do total', alinhar: 'direita', render: (c) => {
                        if (!(relatorio.custoTotalBRL > 0)) return '—';
                        return `${((c.custoTotalBRL / relatorio.custoTotalBRL) * 100).toFixed(1)}%`;
                      } },
                  ]}
                />
              </div>
            )}

            {relatorio.custosPorInsumo?.length > 0 && (
              <div className="card">
                <h2>Onde o dinheiro foi</h2>
                <DataTable
                  densa
                  linhas={relatorio.custosPorInsumo}
                  chaveDe={(i) => `${i.nomeInsumo}-${i.categoria}`}
                  colunas={[
                    { chave: 'nome', titulo: 'Insumo / gasto', papel: 'titulo', render: (i) => i.nomeInsumo },
                    { chave: 'categoria', titulo: 'Categoria', ocultarSeVazia: true,
                      valor: (i) => i.categoria, papel: 'subtitulo',
                      render: (i) => labelCategoria(i.categoria) },
                    { chave: 'qtd', titulo: 'Quantidade', alinhar: 'direita',
                      render: (i) => `${fmt(i.quantidadeTotal)} ${i.unidadeMedida || ''}`.trim() },
                    { chave: 'custo', titulo: 'Custo', alinhar: 'direita', papel: 'destaque',
                      render: (i) => fmtBRL(i.custoTotalBRL) },
                    { chave: 'pct', titulo: '% do total', alinhar: 'direita', render: (i) => {
                        if (!(relatorio.custoTotalBRL > 0)) return '—';
                        const pct = (i.custoTotalBRL / relatorio.custoTotalBRL) * 100;
                        return (
                          <span className="barra-pct">
                            {pct.toFixed(1)}%
                            <span className="barra-pct__trilha">
                              <span className="barra-pct__fill" style={{ width: `${Math.min(pct, 100)}%` }} />
                            </span>
                          </span>
                        );
                      } },
                  ]}
                />
              </div>
            )}

            {aplicacoesPorDia.length > 0 && (
              <div className="card">
                <h2>Manejos por data</h2>
                {aplicacoesPorDia.map((dia) => (
                  <div key={dia.data} className="aplicacoes-dia">
                    <div className="aplicacoes-dia__cab">
                      <span className="aplicacoes-dia__data">{fmtData(dia.data)}</span>
                      <span className="aplicacoes-dia__total">{fmtBRL(dia.total)}</span>
                    </div>
                    <DataTable
                      densa
                      linhas={dia.itens}
                      chaveDe={(a) => a.id}
                      colunas={[
                        { chave: 'desc', titulo: 'Manejo', papel: 'titulo',
                          render: (a) => a.descricao || labelTipoAplicacao(a.tipoAplicacao) },
                        { chave: 'tipo', titulo: 'Tipo', papel: 'chip',
                          render: (a) => labelTipoAplicacao(a.tipoAplicacao) },
                        { chave: 'qtd', titulo: 'Quantidade', alinhar: 'direita',
                          render: (a) => a.quantidade != null ? `${fmt(a.quantidade)} ${a.unidadeMedida || ''}`.trim() : '—' },
                        { chave: 'custo', titulo: 'Custo', alinhar: 'direita', papel: 'destaque',
                          render: (a) => fmtBRL(a.custoTotalBRL) },
                      ]}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        ) : null}
      </Estado>
    </div>
  );
}
